import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../../lib/api";
import { ArrowLeft, Plus, X, RefreshCw } from "lucide-react";

export default function AdminBenchmark() {
  const { benchmarkId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [bench, setBench] = useState(null);
  const [form, setForm] = useState({
    title: "", industry: "", category_phrase: "", region: "United States",
    companies: [{ company_name: "", website: "", competitors: "" }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadList = async () => {
    try { const { data } = await api.get("/admin/benchmarks"); setItems(data.items || []); } catch { /* non-fatal */ }
  };
  const loadOne = async () => {
    if (!benchmarkId) return;
    try { const { data } = await api.get(`/admin/benchmarks/${benchmarkId}`); setBench(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { loadList(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!benchmarkId) return;
    const tick = () => { loadOne(); };
    const tid = setTimeout(tick, 0);
    const iv  = setInterval(tick, 15000);
    return () => { clearTimeout(tid); clearInterval(iv); };
  }, [benchmarkId]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        industry: form.industry.trim(),
        category_phrase: form.category_phrase.trim() || form.industry.trim(),
        region: form.region.trim(),
        companies: form.companies
          .filter((c) => c.company_name.trim() && c.website.trim())
          .map((c) => ({
            company_name: c.company_name.trim(),
            website:      c.website.trim(),
            competitors:  c.competitors.split(",").map((s) => s.trim()).filter(Boolean),
          })),
      };
      if (payload.companies.length < 2) { setErr("Add at least 2 companies."); setBusy(false); return; }
      const { data } = await api.post("/admin/benchmarks", payload);
      navigate(`/admin/benchmarks/${data.benchmark_id}`);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  // ─── DETAIL VIEW ────────────────────────────────────────────────
  if (benchmarkId) {
    if (err) return <div className="min-h-screen bg-[#080807] text-[#c46a5a] p-12">{err}</div>;
    if (!bench) return <div className="min-h-screen bg-[#080807] text-[#c9bca4] p-12">Loading…</div>;
    const b = bench.benchmark;
    return (
      <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="admin-benchmark-detail">
        <Helmet><title>{`${b.title} · Industry Benchmark`}</title></Helmet>
        <main className="max-w-6xl mx-auto px-6 py-12">
          <Link to="/admin/benchmarks" className="text-sm text-[#b8963e] hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> All benchmarks
          </Link>
          <div className="flex items-center justify-between mt-4 mb-8 gap-4 flex-wrap">
            <div>
              <p className="overline mb-2">Industry Benchmark</p>
              <h1 className="font-serif-display text-4xl text-[#f4efe6]" style={{ fontWeight: 600 }}>{b.title}</h1>
              <p className="text-sm text-[#8a7e6d] mt-2 font-mono-eav">
                {b.industry} · &ldquo;{b.category_phrase}&rdquo; · {b.region}
              </p>
            </div>
            <button onClick={loadOne} className="link-pill"><RefreshCw className="w-4 h-4" /> Refresh</button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            <Stat label="Total" value={b.total} />
            <Stat label="Complete" value={b.complete} accent="#5db368" />
            <Stat label="Running" value={b.running} accent="#d4a04a" />
            <Stat label="Failed" value={b.failed} accent="#c46a5a" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="eav-card p-5">
              <p className="overline mb-3">Avg score</p>
              <p className="font-serif-display text-5xl text-[#b8963e]" style={{ fontWeight: 600 }}>
                {bench.aggregate.avg_score ?? "—"}
              </p>
              <p className="text-xs text-[#5e5448] mt-1 font-mono-eav">out of 100</p>
            </div>
            <div className="eav-card p-5">
              <p className="overline mb-3">Tier distribution</p>
              <ul className="space-y-1 text-sm">
                {Object.entries(bench.aggregate.tier_distribution).map(([t, n]) => (
                  <li key={t} className="flex justify-between"><span className="text-[#c9bca4]">{t}</span><span className="font-mono-eav text-[#b8963e]">{n}</span></li>
                ))}
                {Object.keys(bench.aggregate.tier_distribution).length === 0 && <li className="text-[#5e5448] text-xs">No completed audits yet.</li>}
              </ul>
            </div>
            <div className="eav-card p-5">
              <p className="overline mb-3">Primary failure modes</p>
              <ul className="space-y-1 text-sm">
                {Object.entries(bench.aggregate.primary_failure_modes).map(([m, n]) => (
                  <li key={m} className="flex justify-between"><span className="text-[#c9bca4]">{m}</span><span className="font-mono-eav text-[#b8963e]">{n}</span></li>
                ))}
                {Object.keys(bench.aggregate.primary_failure_modes).length === 0 && <li className="text-[#5e5448] text-xs">—</li>}
              </ul>
            </div>
          </div>

          {bench.aggregate.top_ai_surfaced_peers.length > 0 && (
            <div className="mb-12">
              <p className="overline mb-4">Top AI-surfaced peers across the cohort</p>
              <table className="w-full text-sm border border-[#2a2620] rounded-sm overflow-hidden">
                <thead className="bg-[#0c0a08]">
                  <tr className="font-mono-eav text-[10px] tracking-[0.18em] uppercase text-[#8a7e6d]">
                    <th className="text-left px-4 py-3">Company</th>
                    <th className="text-right px-4 py-3">Total mentions</th>
                  </tr>
                </thead>
                <tbody>
                  {bench.aggregate.top_ai_surfaced_peers.map((p) => (
                    <tr key={p.name} className="border-t border-[#2a2620]/60">
                      <td className="px-4 py-2 text-[#f4efe6]">{p.name}</td>
                      <td className="px-4 py-2 text-right font-mono-eav text-[#b8963e]">{p.total_mentions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="overline mb-3 text-[#b8963e]">Audits in this benchmark</p>
          <div className="border border-[#2a2620] rounded-sm overflow-hidden">
            {bench.audits.map((a) => (
              <Link key={a.id} to={`/snapshot/${a.id}`} className="grid grid-cols-12 px-4 py-3 border-b border-[#2a2620]/60 hover:bg-[#0c0a08] text-sm">
                <span className="col-span-6 text-[#f4efe6]">{a.company_name}</span>
                <span className="col-span-2 font-mono-eav text-[#c9bca4]">{a.status}</span>
                <span className="col-span-2 font-mono-eav text-[#b8963e]">{a.total_score ?? "—"}</span>
                <span className="col-span-2 font-mono-eav text-[#8a7e6d]">{a.tier_label ?? "—"}</span>
              </Link>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // ─── LIST + NEW BENCHMARK FORM ──────────────────────────────────
  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="admin-benchmark-list">
      <Helmet><title>Industry Benchmarks · Admin</title></Helmet>
      <main className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/admin" className="text-sm text-[#b8963e] hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Admin dashboard
        </Link>
        <h1 className="font-serif-display text-4xl text-[#f4efe6] mt-4" style={{ fontWeight: 600 }}>Industry Benchmarks</h1>
        <p className="text-sm text-[#8a7e6d] mt-2">Batch admin-bypass snapshot audits for a single category, then aggregate the results into a &ldquo;State of AI Visibility&rdquo; view.</p>

        <form onSubmit={submit} className="mt-10 eav-card p-6" data-testid="benchmark-form">
          <p className="overline mb-4">New benchmark</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title" value={form.title} onChange={(v) => setForm({...form, title: v})} placeholder="e.g. Commercial GCs — Pacific NW" />
            <Field label="Industry" value={form.industry} onChange={(v) => setForm({...form, industry: v})} placeholder="Construction" />
            <Field label="Search category (buyer-intent)" value={form.category_phrase} onChange={(v) => setForm({...form, category_phrase: v})} placeholder="Commercial General Contractor" />
            <Field label="Region" value={form.region} onChange={(v) => setForm({...form, region: v})} />
          </div>

          <p className="overline mt-8 mb-3">Companies in this cohort ({form.companies.length})</p>
          <div className="space-y-3">
            {form.companies.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start">
                <input className="eav-input col-span-4" placeholder="Company name" value={c.company_name}
                       onChange={(e) => { const cs = [...form.companies]; cs[i].company_name = e.target.value; setForm({...form, companies: cs}); }} />
                <input className="eav-input col-span-4" placeholder="https://website" value={c.website}
                       onChange={(e) => { const cs = [...form.companies]; cs[i].website = e.target.value; setForm({...form, companies: cs}); }} />
                <input className="eav-input col-span-3" placeholder="Competitors (comma-separated)" value={c.competitors}
                       onChange={(e) => { const cs = [...form.companies]; cs[i].competitors = e.target.value; setForm({...form, companies: cs}); }} />
                <button type="button" onClick={() => { setForm({...form, companies: form.companies.filter((_, j) => j !== i) }); }}
                        className="col-span-1 p-2 text-[#8a7e6d] hover:text-[#c46a5a]" disabled={form.companies.length <= 1}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setForm({...form, companies: [...form.companies, { company_name: "", website: "", competitors: "" }]})}
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#b8963e] hover:underline" disabled={form.companies.length >= 12}>
            <Plus className="w-4 h-4" /> Add company {form.companies.length >= 12 && " (max 12)"}
          </button>

          {err && <div className="mt-4 p-3 border border-[#c46a5a]/40 bg-[#c46a5a]/10 text-sm text-[#e9a394] rounded-sm">{err}</div>}
          <button type="submit" disabled={busy} className="btn-gold mt-6">
            {busy ? "Launching…" : "Launch benchmark"}
          </button>
        </form>

        {items.length > 0 && (
          <div className="mt-12">
            <p className="overline mb-4">Recent benchmarks</p>
            <ul className="space-y-2">
              {items.map((b) => (
                <li key={b._id}>
                  <Link to={`/admin/benchmarks/${b._id}`} className="block border border-[#2a2620] rounded-sm p-4 hover:border-[#b8963e]">
                    <div className="font-serif-display text-lg text-[#f4efe6]" style={{ fontWeight: 600 }}>{b.title}</div>
                    <div className="text-xs text-[#8a7e6d] font-mono-eav mt-1">{b.category_phrase} · {b.audit_ids?.length || 0} audits · {new Date(b.created_at).toLocaleDateString()}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
      <style>{`
        .link-pill { display:inline-flex; align-items:center; gap:.5rem; padding:.4rem .75rem; border:1px solid #2a2620; color:#c9bca4; border-radius:2px; font-family:ui-monospace,monospace; font-size:11px; letter-spacing:.05em; text-transform:uppercase; transition: border-color .15s, color .15s; }
        .link-pill:hover { border-color:#b8963e; color:#b8963e; }
      `}</style>
    </div>
  );
}

function Stat({ label, value, accent = "#b8963e" }) {
  return (
    <div className="eav-card p-4">
      <p className="text-[10px] font-mono-eav tracking-[0.18em] uppercase font-bold" style={{ color: accent }}>{label}</p>
      <p className="mt-2 font-serif-display font-bold text-3xl text-[#f4efe6]">{value ?? "—"}</p>
    </div>
  );
}
function Field({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-mono-eav tracking-[0.18em] uppercase text-[#8a7e6d]">{label}</span>
      <input className="eav-input mt-1 w-full" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
